import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";

import { Icon } from "@/components";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import { classNames } from "@/utils/classNames";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { api } from "@/redux/api";
import { useAppDispatch } from "@/redux/hooks";

import { ListPerson } from "@/pages/Private/pages/List/schema/list";
import { isRowTakenError } from "@/pages/Private/redux/list/list-person-prospect.api";
import { useFindLeadForProspect } from "@/pages/Private/pages/List/utils/columns/useFindLeadForProspect";

interface SelectProspectActionButtonProps {
	/** The placeholder lead's row; `null` while it is still loading — the button renders disabled. */
	listPerson: ListPerson | null;
}

/**
 * Primary action for a placeholder ("SELECT" / "Person not found") lead: it has no real contact to
 * reach out to, so the reach-out button gives way to the same Select-prospect flow as the feed card
 * slot (FeedSelectProspect) and the list row (SelectProspectCell) — the shared GlobalSearchModal
 * scoped to the lead's company, then the tracker-aware select/replace mutation. On success the Feed
 * tags are invalidated so the run card upgrades and the docked panel re-targets to the chosen
 * person; the `ListPerson` invalidation from the mutation refreshes the panel's chatLead.
 */
export const SelectProspectActionButton: FunctionComponent<SelectProspectActionButtonProps> = ({
	listPerson,
}) => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const { companyName, companyDomain, showSearchModal, setShowSearchModal, handleConfirm } =
		useFindLeadForProspect(listPerson);

	const handleConfirmSelection = async (linkedinUrls: string[]) => {
		try {
			await handleConfirm(linkedinUrls);
			dispatch(api.util.invalidateTags(["Feed", "FeedCounts"]));
		} catch (error) {
			// Someone filled the row meanwhile (409): say so in a toast, close the search and refresh
			// the Feed to show them (PREC-4570). The refresh re-targets the panel to the filled lead,
			// which unmounts this button with its search modal, so the modal's snackbar would vanish
			// with it (QA comment 27611). The modal closes itself on success.
			if (isRowTakenError(error)) {
				showGlobalToast(t("errors.rowNoLongerPlaceholder"));
				setShowSearchModal(false);
				// The lead too, as the mutation does on success: otherwise the panel keeps the
				// placeholder and offers "Select prospect" again for a filled row (QA comment 27628).
				dispatch(api.util.invalidateTags(["Feed", "FeedCounts", "ListPerson"]));

				return;
			}

			throw error;
		}
	};

	return (
		<>
			<button
				className={classNames(
					"flex h-8 flex-[2] cursor-pointer items-center justify-center gap-1.5 rounded-[10px] border border-transparent bg-blue-action px-2.5 text-xs font-semibold text-white duration-200",
					"hover:bg-blue-action-dark hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-action disabled:hover:shadow-none"
				)}
				data-testid="lead-panel-select-prospect"
				disabled={!listPerson}
				type="button"
				onClick={() => setShowSearchModal(true)}
			>
				<Icon className="w-3.5" icon={faUserPlus} />
				<span className="truncate">{t("list.selectProspect")}</span>
			</button>
			{showSearchModal && (
				<GlobalSearchModal
					singleSelect
					confirmLabel={t("list.selectProspect")}
					initialFilterCompany={
						companyDomain ? { name: companyName, domain: companyDomain } : undefined
					}
					isOpened={showSearchModal}
					setIsOpened={setShowSearchModal}
					onConfirmSelection={handleConfirmSelection}
				/>
			)}
		</>
	);
};
